/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';

import { Module, Node } from '@msdyn365-commerce-modules/utilities';

// import { ICheckoutState } from '@msdyn365-commerce/global-state';
import {
    IAddressAddItem,
    IAddressAddUpdateProps
} from '../../../modules/dobbies-checkout-shipping-address/./common/components/address-add';
import {
    IAddressSelectItem,
    IAddressSelectProps
} from '../../../modules/dobbies-checkout-shipping-address/./common/components/address-select';
import { IAddressShowItem, IAddressShowProps } from '../../../modules/dobbies-checkout-shipping-address/./common/components/address-show';
import { ICheckoutShippingAddressViewProps } from '../../../modules/dobbies-checkout-shipping-address/./dobbies-checkout-shipping-address';
import { IDobbiesCheckoutShippingAddressProps } from '../definition-extensions/dobbies-checkout-shipping-address.ext.props.autogenerated';

const AddressShow: React.FC<IAddressShowProps> = ({ AddressDetail, items }) => {
    return (
        <Node {...AddressDetail}>
            {items &&
                items.map((item: IAddressShowItem) => {
                    const { key, description } = item;
                    if (key.toLowerCase() !== 'threeletterisoregionname') {
                        return <>{description}</>;
                    }
                    return;
                })}
        </Node>
    );
};

const AddressSelect: React.FC<IAddressSelectProps> = ({
    SelectAddress,
    addButton,
    items,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton
}) => {
    return (
        <Node {...SelectAddress}>
            {addButton}
            {items &&
                items.map((item: IAddressSelectItem) => {
                    const SelectItem = item.SelectItem;
                    return (
                        <Node {...SelectItem} key={item.key}>
                            {item.input}
                            <AddressShow {...item.showItems} />
                        </Node>
                    );
                })}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

const AddressAddUpdate: React.FC<IAddressAddUpdateProps & ICheckoutShippingAddressViewProps> = ({
    AddressForm,
    heading,
    items,
    hasError,
    error,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton,
    renderInputWithButton
}) => {
    return (
        <Node {...AddressForm}>
            {heading}
            {/* VSI-Customization -- START Render input field for postcode and button  */}
            {renderInputWithButton}
            {/* VSI-Customization -- END  */}
            {items &&
                items.map((item: IAddressAddItem) => {
                    const { AddressItem, key, label, alert, input } = item;
                    if (key.toLowerCase() !== 'threeletterisoregionname') {
                        return (
                            <Node {...AddressItem} key={key}>
                                {label}
                                {alert}
                                {input}
                            </Node>
                        );
                    } else {
                        return;
                    }
                })}
            {hasError && (
                <Node {...error.AddressError}>
                    {error.title}
                    {error.message}
                </Node>
            )}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

const CheckoutShippingAddressView: React.FC<ICheckoutShippingAddressViewProps & IDobbiesCheckoutShippingAddressProps<{}>> = props => {
    const { CheckoutShippingAddress, viewState, showAddress, showAddressSelect, showAddOrUpdateAddress } = props;

    React.useEffect(() => {
        if (viewState.isShowAddress !== false) {
            const checkoutState = props.data.checkout.result?.checkoutCart!;
            const updateCartDeliverySpecificationInput = {
                deliveryModeId: props.config.modeOfDeliveryId!,
                shippingAddress: viewState.isShowAddress
            };
            const updateAndProceedFromDeliveryOptions = async () => {
                if (viewState.isShowAddress !== undefined) {
                    await checkoutState
                        .updateCartDeliverySpecification(updateCartDeliverySpecificationInput)
                        .then(response => {
                            if (response.status === 'FAILED') {
                                console.log('delivery options selection failed or doesnt exist.');
                            } else {
                                console.log('Delivery option call has been completed');
                            }
                        })
                        .catch(() => {
                            console.log(`Couldn't updated delivery options`);
                        });
                }
            };
            const updateEmail = async () => {
                let emailAddress = (await props.data.customerInformation).Email;
                emailAddress = emailAddress && emailAddress.toString();
                if (emailAddress) {
                    (await props.data.checkout) &&
                        props.data.checkout.result &&
                        props.data.checkout.result.updateGuestCheckoutEmail({ newGuestCheckoutEmail: emailAddress });
                }
            };
            // tslint:disable-next-line: no-floating-promises
            updateAndProceedFromDeliveryOptions();
            updateEmail();
        }
    }, [viewState.isShowAddress]);

    return (
        <Module {...CheckoutShippingAddress}>
            {viewState.isShowAddress && <AddressShow {...showAddress} />}
            {viewState.isShowAddresList && <AddressSelect {...showAddressSelect} />}
            {viewState.isShowAddOrUpdateAddress && <AddressAddUpdate {...showAddOrUpdateAddress} {...props} />}
        </Module>
    );
};

export default CheckoutShippingAddressView;
